// Benefits module — Overview, Plans, Providers, and per-employee management of
// Enrollments / Allowances / Deductions / Retirement. Gated by tenant settings:
// the page shows a "not enabled" notice when benefits_enabled is false (the full
// module is hidden via the sidebar; allowances/deductions surface under Payroll).
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/lib/apiError';
import { isValidPhone, PHONE_HINT } from '@/features/shared/validation/phone';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '@/features/shared/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/features/shared/components/ui/tabs';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/features/shared/components/ui/table';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/features/shared/components/ui/dialog';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import {
  HeartPulse, ShieldCheck, PiggyBank, Wallet, MinusCircle, Plus, Loader2, Building2,
} from 'lucide-react';
import {
  getBenefitsSettings, type BenefitsSettings,
  listPlans, createPlan, updatePlan, deletePlan, activatePlan, archivePlan, type BenefitPlan,
  listProviders, createProvider, type BenefitProvider,
  listEnrollments, hrEnroll, approveEnrollment, terminateEnrollment, type BenefitEnrollment,
  listAllowances, createAllowance, approveAllowance, endAllowance, listAllAllowances, type EmployeeAllowance,
  listDeductions, createDeduction, approveDeduction, endDeduction, listAllDeductions, type EmployeeDeduction,
  listRetirement, createRetirement, endRetirement, type RetirementContribution,
  listCompanyAllowances, createCompanyAllowance, deleteCompanyAllowance, type CompanyAllowance,
  getAllEmployees,
} from '@/lib/hrApi';

type Employee = { id: string; first_name?: string; last_name?: string; full_name?: string; email?: string };

const empDisplayName = (e: Employee) =>
  e.full_name || [e.first_name, e.last_name].filter(Boolean).join(' ') || e.email || e.id;

// Free-text name input with employee-search suggestions — type to filter the
// tenant roster; picking a match fills the field with that employee's name. The
// stored value is still a plain string (any free text is allowed).
const EmployeeNameInput: React.FC<{ value: string; onChange: (v: string) => void; placeholder?: string }> = ({ value, onChange, placeholder }) => {
  const [emps, setEmps] = useState<Employee[]>([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { getAllEmployees().then((e: any) => setEmps(e || [])).catch(() => {}); }, []);
  const q = (value || '').trim().toLowerCase();
  const matches = q.length < 1 ? [] : emps
    .map((e) => ({ e, n: empDisplayName(e) }))
    .filter(({ e, n }) => n.toLowerCase().includes(q) || (e.email || '').toLowerCase().includes(q))
    .slice(0, 8);
  return (
    <div className="relative">
      <Input
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && matches.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-56 overflow-auto rounded-md border border-border bg-popover shadow-md">
          {matches.map(({ e, n }) => (
            <button
              type="button"
              key={e.id}
              className="w-full text-left px-3 py-2 text-sm hover:bg-accent flex items-center justify-between gap-2"
              onMouseDown={(ev) => { ev.preventDefault(); onChange(n); setOpen(false); }}
            >
              <span className="truncate">{n}</span>
              {e.email && <span className="text-xs text-muted-foreground truncate">{e.email}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const fmt = (n: number, c = 'TZS') => `${c} ${Number(n || 0).toLocaleString()}`;

/** Run a row action (approve/end/terminate…): reload the table on success,
 *  surface the backend's explanation in a toast on failure. */
const runAction = (p: Promise<unknown>, reload: () => void, fallback: string) =>
  p.then(reload).catch((e) => toast.error(getApiErrorMessage(e, fallback), { duration: 6000 }));

const STATUS_COLOR: Record<string, string> = {
  active: 'bg-success-soft text-success-strong', approved: 'bg-success-soft text-success-strong',
  pending: 'bg-warning-soft text-warning-strong', draft: 'bg-slate-100 text-slate-600',
  declined: 'bg-danger-soft text-danger-strong', terminated: 'bg-danger-soft text-danger-strong',
  ended: 'bg-slate-100 text-slate-500', suspended: 'bg-warning-soft text-warning-strong',
  archived: 'bg-slate-100 text-slate-500', inactive: 'bg-slate-100 text-slate-500',
};
const StatusBadge: React.FC<{ s: string }> = ({ s }) => (
  <Badge className={`${STATUS_COLOR[s] || 'bg-slate-100 text-slate-600'} border-0 capitalize`}>{s}</Badge>
);

export const BenefitsPage: React.FC = () => {
  const { t } = useTranslation('benefits');
  const [settings, setSettings] = useState<BenefitsSettings | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBenefitsSettings().then(setSettings).catch(() => setSettings(null)).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex items-center gap-2 text-muted-foreground p-8"><Loader2 className="w-4 h-4 animate-spin" /> {t('loading')}</div>;
  }

  const full = !!settings?.benefits_enabled;
  const showAllow = !!settings?.allowances_enabled;
  const showDeduct = !!settings?.deductions_enabled;

  // Nothing enabled at all → notice.
  if (!full && !showAllow && !showDeduct) {
    return (
      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><HeartPulse className="w-5 h-5 text-orange-600" /> {t('title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">{t('not_enabled')}</p>
          <Button asChild variant="outline"><Link to="../settings">{t('open_settings')}</Link></Button>
        </CardContent>
      </Card>
    );
  }

  const defaultTab = full ? 'overview' : showAllow ? 'allowances' : 'deductions';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-charcoal flex items-center gap-2">
          <HeartPulse className="w-6 h-6 text-orange-600" /> {t('title')}
        </h1>
        <p className="text-muted-foreground">{full ? t('subtitle_full') : t('subtitle_min')}</p>
      </div>

      {!full && (
        <div className="rounded-lg border border-warning bg-warning-soft p-3 text-sm text-warning-strong">
          {t('partial_banner')}
        </div>
      )}

      <Tabs defaultValue={defaultTab}>
        <TabsList className="flex-wrap h-auto">
          {full && <TabsTrigger value="overview">{t('tab_overview')}</TabsTrigger>}
          {full && <TabsTrigger value="plans">{t('tab_plans')}</TabsTrigger>}
          {full && <TabsTrigger value="providers">{t('tab_providers')}</TabsTrigger>}
          {showAllow && <TabsTrigger value="allowances">{t('allowances')}</TabsTrigger>}
          {showAllow && <TabsTrigger value="company">{t('tab_company', 'Company Benefits')}</TabsTrigger>}
          {showDeduct && <TabsTrigger value="deductions">{t('deductions')}</TabsTrigger>}
          <TabsTrigger value="employee">{t('tab_employee')}</TabsTrigger>
        </TabsList>

        {full && <TabsContent value="overview" className="mt-4"><Overview settings={settings!} /></TabsContent>}
        {full && <TabsContent value="plans" className="mt-4"><Plans /></TabsContent>}
        {full && <TabsContent value="providers" className="mt-4"><Providers /></TabsContent>}
        {showAllow && <TabsContent value="allowances" className="mt-4"><TenantAllowances /></TabsContent>}
        {showAllow && <TabsContent value="company" className="mt-4"><CompanyBenefits /></TabsContent>}
        {showDeduct && <TabsContent value="deductions" className="mt-4"><TenantDeductions /></TabsContent>}
        <TabsContent value="employee" className="mt-4"><EmployeeBenefits settings={settings!} /></TabsContent>
      </Tabs>
    </div>
  );
};

// ---- Overview ---------------------------------------------------------------
const Overview: React.FC<{ settings: BenefitsSettings }> = ({ settings }) => {
  const { t } = useTranslation('benefits');
  const [plans, setPlans] = useState<BenefitPlan[]>([]);
  const [providers, setProviders] = useState<BenefitProvider[]>([]);
  useEffect(() => { listPlans().then(setPlans).catch(() => {}); listProviders().then(setProviders).catch(() => {}); }, []);
  const active = plans.filter((p) => p.status === 'active').length;
  const cards = [
    { icon: ShieldCheck, label: t('benefit_plans'), value: `${plans.length}`, sub: t('active_count', { count: active }) },
    { icon: Building2, label: t('providers'), value: `${providers.length}`, sub: '' },
  ];
  const flags: Array<[string, boolean]> = [
    [t('flag_insurance'), settings.insurance_enabled], [t('retirement'), settings.retirement_enabled],
    [t('allowances'), settings.allowances_enabled], [t('deductions'), settings.deductions_enabled],
    [t('flag_self_enroll'), settings.self_enrollment_enabled], [t('flag_hr_approval'), settings.hr_approval_required],
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c) => (
          <Card key={c.label}><CardContent className="pt-6">
            <c.icon className="w-5 h-5 text-orange-600 mb-2" />
            <div className="text-2xl font-bold">{c.value}</div>
            <div className="text-xs text-muted-foreground">{c.label}{c.sub ? ` · ${c.sub}` : ''}</div>
          </CardContent></Card>
        ))}
      </div>
      <Card><CardHeader><CardTitle className="text-base">{t('configuration')}</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {flags.map(([l, on]) => (
            <Badge key={l} className={`${on ? 'bg-success-soft text-success-strong' : 'bg-slate-100 text-slate-500'} border-0`}>{l}: {on ? t('on') : t('off')}</Badge>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

// ---- generic modal form -----------------------------------------------------
const FormModal: React.FC<{ open: boolean; title: string; onClose: () => void; onSubmit: () => void; busy?: boolean; children: React.ReactNode }> = ({ open, title, onClose, onSubmit, busy, children }) => {
  const { t } = useTranslation('benefits');
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">{children}</div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('cancel')}</Button>
          <Button onClick={onSubmit} disabled={busy}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t('save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="space-y-1"><Label className="text-xs">{label}</Label>{children}</div>
);
const sel = 'w-full h-9 rounded-md border border-border bg-background px-2 text-sm';

// ---- Plans ------------------------------------------------------------------
const PLAN_TYPES = ['medical_insurance', 'life_insurance', 'retirement_fund', 'pension', 'wellness', 'training', 'other'];
const IMPACTS = ['none', 'allowance', 'deduction', 'employer_contribution'];
const Plans: React.FC = () => {
  const { t } = useTranslation('benefits');
  const [rows, setRows] = useState<BenefitPlan[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const emptyForm = { type: 'medical_insurance', contribution_type: 'fixed_amount', payroll_impact: 'none' };
  const [f, setF] = useState<any>(emptyForm);
  const load = () => listPlans().then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);
  const openNew = () => { setEditId(null); setF(emptyForm); setOpen(true); };
  const openEdit = (p: BenefitPlan) => { setEditId(p.id); setF({ ...p }); setOpen(true); };
  const save = async () => {
    setBusy(true);
    const payload = { ...f, employee_contribution: Number(f.employee_contribution || 0), employer_contribution: Number(f.employer_contribution || 0), taxable: !!f.taxable };
    try {
      if (editId) await updatePlan(editId, payload);
      else await createPlan(payload);
      setOpen(false); setEditId(null); setF(emptyForm); load();
    }
    catch (e) { toast.error(getApiErrorMessage(e, editId ? t('err_update_plan', 'Could not update the plan.') : t('err_create_plan', 'Could not create the plan.')), { duration: 6000 }); }
    finally { setBusy(false); }
  };
  const act = (fn: Promise<any>) => runAction(fn, load, t('err_update_plan', 'Could not update the plan.'));
  const [deleteTarget, setDeleteTarget] = useState<BenefitPlan | null>(null);
  const confirmDelete = () => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    setDeleteTarget(null);
    runAction(deletePlan(id), load, t('err_delete_plan', 'Could not delete the plan.'));
  };
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-base">{t('benefit_plans')}</CardTitle>
        <Button size="sm" onClick={openNew}><Plus className="w-4 h-4 mr-1" /> {t('new_plan')}</Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow><TableHead>{t('col_name')}</TableHead><TableHead>{t('col_type')}</TableHead><TableHead>{t('col_impact')}</TableHead><TableHead>{t('col_ee_er')}</TableHead><TableHead>{t('col_status')}</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {rows.map((p) => (
              <TableRow key={p.id}>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell className="capitalize">{p.type.replace(/_/g, ' ')}</TableCell>
                <TableCell className="capitalize">{p.payroll_impact.replace(/_/g, ' ')}</TableCell>
                <TableCell>{fmt(p.employee_contribution)} / {fmt(p.employer_contribution)}</TableCell>
                <TableCell><StatusBadge s={p.status} /></TableCell>
                <TableCell className="text-right space-x-2">
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)}>{t('edit', 'Edit')}</Button>
                  {p.status !== 'active' && <Button size="sm" variant="outline" onClick={() => act(activatePlan(p.id))}>{t('activate')}</Button>}
                  {p.status !== 'archived' && <Button size="sm" variant="ghost" onClick={() => act(archivePlan(p.id))}>{t('archive')}</Button>}
                  <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => setDeleteTarget(p)}>{t('delete', 'Delete')}</Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">{t('no_plans')}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
      <FormModal open={open} title={editId ? t('edit_benefit_plan', 'Edit benefit plan') : t('new_benefit_plan')} busy={busy} onClose={() => { setOpen(false); setEditId(null); }} onSubmit={save}>
        <Field label={t('col_name')}><EmployeeNameInput value={f.name || ''} onChange={(v) => setF({ ...f, name: v })} placeholder={t('plan_name_ph', 'Search an employee or type a plan name…')} /></Field>
        <Field label={t('col_type')}><select className={sel} value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>{PLAN_TYPES.map((tp) => <option key={tp} value={tp}>{tp.replace(/_/g, ' ')}</option>)}</select></Field>
        <Field label={t('f_payroll_impact')}><select className={sel} value={f.payroll_impact} onChange={(e) => setF({ ...f, payroll_impact: e.target.value })}>{IMPACTS.map((tp) => <option key={tp} value={tp}>{tp.replace(/_/g, ' ')}</option>)}</select></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('f_employee_contribution')}><Input type="number" value={f.employee_contribution || ''} onChange={(e) => setF({ ...f, employee_contribution: e.target.value })} /></Field>
          <Field label={t('f_employer_contribution')}><Input type="number" value={f.employer_contribution || ''} onChange={(e) => setF({ ...f, employer_contribution: e.target.value })} /></Field>
        </div>
        <Field label={t('f_contribution_type')}><select className={sel} value={f.contribution_type} onChange={(e) => setF({ ...f, contribution_type: e.target.value })}><option value="fixed_amount">{t('opt_fixed_amount')}</option><option value="percentage">{t('opt_percentage')}</option></select></Field>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.taxable} onChange={(e) => setF({ ...f, taxable: e.target.checked })} /> {t('taxable_benefit')}</label>
      </FormModal>

      {/* In-app delete confirmation (never the native window.confirm). */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('delete_plan_title', 'Delete benefit plan?')}</DialogTitle></DialogHeader>
          <p className="py-1 text-sm text-muted-foreground">
            {t('confirm_delete_plan', 'Permanently delete "{{name}}"? This cannot be undone.', { name: deleteTarget?.name ?? '' })}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>{t('cancel')}</Button>
            <Button className="bg-red-600 text-white hover:bg-red-700" onClick={confirmDelete}>{t('delete', 'Delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ---- Providers --------------------------------------------------------------
const PROVIDER_TYPES = ['insurance', 'pension', 'wellness', 'training', 'other'];
const Providers: React.FC = () => {
  const { t } = useTranslation('benefits');
  const [rows, setRows] = useState<BenefitProvider[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState<any>({ type: 'insurance' });
  const load = () => listProviders().then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (f.contact_phone?.trim() && !isValidPhone(f.contact_phone)) { toast.error(t('phone_invalid', { defaultValue: PHONE_HINT })); return; }
    setBusy(true); try { await createProvider(f); setOpen(false); setF({ type: 'insurance' }); load(); } catch (e) { toast.error(getApiErrorMessage(e, t('err_create_provider', 'Could not create the provider.')), { duration: 6000 }); } finally { setBusy(false); }
  };
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-base">{t('providers')}</CardTitle>
        <Button size="sm" onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1" /> {t('new_provider')}</Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow><TableHead>{t('col_name')}</TableHead><TableHead>{t('col_type')}</TableHead><TableHead>{t('col_contact')}</TableHead><TableHead>{t('col_status')}</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((p) => (
              <TableRow key={p.id}><TableCell className="font-medium">{p.name}</TableCell><TableCell className="capitalize">{p.type}</TableCell><TableCell className="text-muted-foreground">{p.contact_email || p.contact_phone || '—'}</TableCell><TableCell><StatusBadge s={p.status} /></TableCell></TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">{t('no_providers')}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
      <FormModal open={open} title={t('new_provider')} busy={busy} onClose={() => setOpen(false)} onSubmit={save}>
        <Field label={t('col_name')}><Input value={f.name || ''} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label={t('col_type')}><select className={sel} value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>{PROVIDER_TYPES.map((tp) => <option key={tp} value={tp}>{tp}</option>)}</select></Field>
        <Field label={t('f_contact_email')}><Input value={f.contact_email || ''} onChange={(e) => setF({ ...f, contact_email: e.target.value })} /></Field>
        <Field label={t('f_contact_phone')}><Input value={f.contact_phone || ''} onChange={(e) => setF({ ...f, contact_phone: e.target.value })} /></Field>
        <Field label={t('f_website')}><Input value={f.website || ''} onChange={(e) => setF({ ...f, website: e.target.value })} /></Field>
      </FormModal>
    </Card>
  );
};

// ---- Employee benefits (per-employee) --------------------------------------
const EmployeeBenefits: React.FC<{ settings: BenefitsSettings }> = ({ settings }) => {
  const { t } = useTranslation('benefits');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [empId, setEmpId] = useState('');
  useEffect(() => { getAllEmployees().then((e: any) => setEmployees(e || [])).catch(() => {}); }, []);
  const name = (e: Employee) => e.full_name || [e.first_name, e.last_name].filter(Boolean).join(' ') || e.email || e.id;

  return (
    <div className="space-y-4">
      <Card><CardContent className="pt-6">
        <Field label={t('f_employee')}>
          <select className={sel + ' max-w-md'} value={empId} onChange={(e) => setEmpId(e.target.value)}>
            <option value="">{t('select_employee')}</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{name(e)}</option>)}
          </select>
        </Field>
      </CardContent></Card>

      {empId && (
        <Tabs defaultValue={settings.allowances_enabled ? 'allowances' : settings.deductions_enabled ? 'deductions' : settings.retirement_enabled ? 'retirement' : 'enrollments'}>
          <TabsList>
            {settings.allowances_enabled && <TabsTrigger value="allowances"><Wallet className="w-4 h-4 mr-1" /> {t('allowances')}</TabsTrigger>}
            {settings.deductions_enabled && <TabsTrigger value="deductions"><MinusCircle className="w-4 h-4 mr-1" /> {t('deductions')}</TabsTrigger>}
            {settings.retirement_enabled && <TabsTrigger value="retirement"><PiggyBank className="w-4 h-4 mr-1" /> {t('retirement')}</TabsTrigger>}
            {settings.benefits_enabled && <TabsTrigger value="enrollments"><ShieldCheck className="w-4 h-4 mr-1" /> {t('tab_enrollments')}</TabsTrigger>}
          </TabsList>
          {settings.allowances_enabled && <TabsContent value="allowances" className="mt-4"><Allowances employeeId={empId} /></TabsContent>}
          {settings.deductions_enabled && <TabsContent value="deductions" className="mt-4"><Deductions employeeId={empId} /></TabsContent>}
          {settings.retirement_enabled && <TabsContent value="retirement" className="mt-4"><Retirement employeeId={empId} /></TabsContent>}
          {settings.benefits_enabled && <TabsContent value="enrollments" className="mt-4"><Enrollments employeeId={empId} /></TabsContent>}
        </Tabs>
      )}
    </div>
  );
};

const ALLOWANCE_TYPES = ['housing', 'transport', 'meal', 'fuel', 'internet', 'airtime', 'other'];
const FREQ = ['monthly', 'one_time', 'quarterly', 'yearly'];
const Allowances: React.FC<{ employeeId: string }> = ({ employeeId }) => {
  const { t } = useTranslation('benefits');
  const [rows, setRows] = useState<EmployeeAllowance[]>([]);
  const [open, setOpen] = useState(false); const [busy, setBusy] = useState(false);
  const [f, setF] = useState<any>({ allowance_type: 'transport', frequency: 'monthly', taxable: true });
  const load = () => listAllowances(employeeId).then(setRows).catch(() => {});
  useEffect(() => { load(); }, [employeeId]);
  const save = async () => { setBusy(true); try { await createAllowance(employeeId, { ...f, amount: Number(f.amount || 0) }); setOpen(false); setF({ allowance_type: 'transport', frequency: 'monthly', taxable: true }); load(); } catch (e) { toast.error(getApiErrorMessage(e, t('err_create_allowance', 'Could not add the allowance.')), { duration: 6000 }); } finally { setBusy(false); } };
  const act = (p: Promise<any>) => runAction(p, load, t('err_update_allowance', 'Could not update the allowance.'));
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between"><CardTitle className="text-base">{t('allowances')}</CardTitle><Button size="sm" onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1" /> {t('add')}</Button></CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow><TableHead>{t('col_name')}</TableHead><TableHead>{t('col_type')}</TableHead><TableHead>{t('col_amount')}</TableHead><TableHead>{t('col_taxable')}</TableHead><TableHead>{t('col_status')}</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {rows.map((a) => (
              <TableRow key={a.id}><TableCell className="font-medium">{a.name}</TableCell><TableCell className="capitalize">{a.allowance_type}</TableCell><TableCell>{fmt(a.amount, a.currency)}</TableCell><TableCell>{a.taxable ? t('yes') : t('no')}</TableCell><TableCell><StatusBadge s={a.status} /></TableCell>
                <TableCell className="text-right space-x-2">{a.status === 'draft' && <Button size="sm" variant="outline" onClick={() => act(approveAllowance(a.id))}>{t('approve')}</Button>}{a.status !== 'ended' && <Button size="sm" variant="ghost" onClick={() => act(endAllowance(a.id))}>{t('end')}</Button>}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">{t('no_allowances')}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
      <FormModal open={open} title={t('add_allowance')} busy={busy} onClose={() => setOpen(false)} onSubmit={save}>
        <Field label={t('col_type')}><select className={sel} value={f.allowance_type} onChange={(e) => setF({ ...f, allowance_type: e.target.value })}>{ALLOWANCE_TYPES.map((tp) => <option key={tp} value={tp}>{tp}</option>)}</select></Field>
        <Field label={t('col_name')}><Input value={f.name || ''} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label={t('col_amount')}><Input type="number" value={f.amount || ''} onChange={(e) => setF({ ...f, amount: e.target.value })} /></Field>
        <Field label={t('col_frequency')}><select className={sel} value={f.frequency} onChange={(e) => setF({ ...f, frequency: e.target.value })}>{FREQ.map((tp) => <option key={tp} value={tp}>{tp.replace('_', ' ')}</option>)}</select></Field>
        <Field label={t('f_effective_start')}><Input type="date" value={f.effective_start_date || ''} onChange={(e) => setF({ ...f, effective_start_date: e.target.value })} /></Field>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.taxable} onChange={(e) => setF({ ...f, taxable: e.target.checked })} /> {t('taxable')}</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.in_kind} onChange={(e) => setF({ ...f, in_kind: e.target.checked })} /> {t('in_kind_benefit', 'Benefit in kind (non-cash)')}</label>
      </FormModal>
    </Card>
  );
};

const DEDUCTION_TYPES = ['insurance_premium', 'loan_repayment', 'retirement_contribution', 'union_fee', 'other'];
const Deductions: React.FC<{ employeeId: string }> = ({ employeeId }) => {
  const { t } = useTranslation('benefits');
  const [rows, setRows] = useState<EmployeeDeduction[]>([]);
  const [open, setOpen] = useState(false); const [busy, setBusy] = useState(false);
  const [f, setF] = useState<any>({ deduction_type: 'union_fee', frequency: 'monthly', pre_tax: false });
  const load = () => listDeductions(employeeId).then(setRows).catch(() => {});
  useEffect(() => { load(); }, [employeeId]);
  const save = async () => { setBusy(true); try { await createDeduction(employeeId, { ...f, amount: Number(f.amount || 0) }); setOpen(false); setF({ deduction_type: 'union_fee', frequency: 'monthly', pre_tax: false }); load(); } catch (e) { toast.error(getApiErrorMessage(e, t('err_create_deduction', 'Could not add the deduction.')), { duration: 6000 }); } finally { setBusy(false); } };
  const act = (p: Promise<any>) => runAction(p, load, t('err_update_deduction', 'Could not update the deduction.'));
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between"><CardTitle className="text-base">{t('deductions')}</CardTitle><Button size="sm" onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1" /> {t('add')}</Button></CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow><TableHead>{t('col_name')}</TableHead><TableHead>{t('col_type')}</TableHead><TableHead>{t('col_amount')}</TableHead><TableHead>{t('col_pretax')}</TableHead><TableHead>{t('col_status')}</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {rows.map((d) => (
              <TableRow key={d.id}><TableCell className="font-medium">{d.name}</TableCell><TableCell className="capitalize">{d.deduction_type.replace(/_/g, ' ')}</TableCell><TableCell>{fmt(d.amount, d.currency)}</TableCell><TableCell>{d.pre_tax ? t('yes') : t('no')}</TableCell><TableCell><StatusBadge s={d.status} /></TableCell>
                <TableCell className="text-right space-x-2">{d.status === 'draft' && <Button size="sm" variant="outline" onClick={() => act(approveDeduction(d.id))}>{t('approve')}</Button>}{d.status !== 'ended' && <Button size="sm" variant="ghost" onClick={() => act(endDeduction(d.id))}>{t('end')}</Button>}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">{t('no_deductions')}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
      <FormModal open={open} title={t('add_deduction')} busy={busy} onClose={() => setOpen(false)} onSubmit={save}>
        <Field label={t('col_type')}><select className={sel} value={f.deduction_type} onChange={(e) => setF({ ...f, deduction_type: e.target.value })}>{DEDUCTION_TYPES.map((tp) => <option key={tp} value={tp}>{tp.replace(/_/g, ' ')}</option>)}</select></Field>
        <Field label={t('col_name')}><Input value={f.name || ''} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label={t('col_amount')}><Input type="number" value={f.amount || ''} onChange={(e) => setF({ ...f, amount: e.target.value })} /></Field>
        <Field label={t('col_frequency')}><select className={sel} value={f.frequency} onChange={(e) => setF({ ...f, frequency: e.target.value })}>{FREQ.map((tp) => <option key={tp} value={tp}>{tp.replace('_', ' ')}</option>)}</select></Field>
        <Field label={t('f_effective_start')}><Input type="date" value={f.effective_start_date || ''} onChange={(e) => setF({ ...f, effective_start_date: e.target.value })} /></Field>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.pre_tax} onChange={(e) => setF({ ...f, pre_tax: e.target.checked })} /> {t('col_pretax')}</label>
      </FormModal>
    </Card>
  );
};

const Retirement: React.FC<{ employeeId: string }> = ({ employeeId }) => {
  const { t } = useTranslation('benefits');
  const [rows, setRows] = useState<RetirementContribution[]>([]);
  const [open, setOpen] = useState(false); const [busy, setBusy] = useState(false);
  const [f, setF] = useState<any>({ retirement_provider: 'NSSF', contribution_type: 'percentage' });
  const load = () => listRetirement(employeeId).then(setRows).catch(() => {});
  useEffect(() => { load(); }, [employeeId]);
  const save = async () => { setBusy(true); try { await createRetirement(employeeId, { ...f, employee_contribution_rate: Number(f.employee_contribution_rate || 0), employer_contribution_rate: Number(f.employer_contribution_rate || 0) }); setOpen(false); setF({ retirement_provider: 'NSSF', contribution_type: 'percentage' }); load(); } catch (e) { toast.error(getApiErrorMessage(e, t('err_create_retirement', 'Could not add the retirement contribution.')), { duration: 6000 }); } finally { setBusy(false); } };
  const act = (p: Promise<any>) => runAction(p, load, t('err_update_retirement', 'Could not update the retirement contribution.'));
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between"><CardTitle className="text-base">{t('retirement_contributions')}</CardTitle><Button size="sm" onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1" /> {t('add')}</Button></CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow><TableHead>{t('col_provider')}</TableHead><TableHead>{t('col_membership')}</TableHead><TableHead>{t('col_ee')}</TableHead><TableHead>{t('col_er')}</TableHead><TableHead>{t('col_status')}</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {rows.map((c) => (
              <TableRow key={c.id}><TableCell className="font-medium">{c.retirement_provider}</TableCell><TableCell className="text-muted-foreground">{c.membership_number || '—'}</TableCell><TableCell>{c.employee_contribution_rate}</TableCell><TableCell>{c.employer_contribution_rate}</TableCell><TableCell><StatusBadge s={c.status} /></TableCell>
                <TableCell className="text-right">{c.status !== 'ended' && <Button size="sm" variant="ghost" onClick={() => act(endRetirement(c.id))}>{t('end')}</Button>}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">{t('no_retirement')}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
      <FormModal open={open} title={t('add_retirement')} busy={busy} onClose={() => setOpen(false)} onSubmit={save}>
        <Field label={t('col_provider')}><select className={sel} value={f.retirement_provider} onChange={(e) => setF({ ...f, retirement_provider: e.target.value })}><option value="NSSF">NSSF</option><option value="PSSSF">PSSSF</option><option value="other">other</option></select></Field>
        <Field label={t('f_membership_number')}><Input value={f.membership_number || ''} onChange={(e) => setF({ ...f, membership_number: e.target.value })} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('f_employee_rate')}><Input type="number" value={f.employee_contribution_rate || ''} onChange={(e) => setF({ ...f, employee_contribution_rate: e.target.value })} /></Field>
          <Field label={t('f_employer_rate')}><Input type="number" value={f.employer_contribution_rate || ''} onChange={(e) => setF({ ...f, employer_contribution_rate: e.target.value })} /></Field>
        </div>
        <Field label={t('f_contribution_type')}><select className={sel} value={f.contribution_type} onChange={(e) => setF({ ...f, contribution_type: e.target.value })}><option value="percentage">{t('opt_percentage')}</option><option value="fixed_amount">{t('opt_fixed_amount')}</option></select></Field>
        <Field label={t('f_effective_start')}><Input type="date" value={f.effective_start_date || ''} onChange={(e) => setF({ ...f, effective_start_date: e.target.value })} /></Field>
      </FormModal>
    </Card>
  );
};

const COVERAGE = ['employee_only', 'employee_spouse', 'employee_children', 'family'];
const Enrollments: React.FC<{ employeeId: string }> = ({ employeeId }) => {
  const { t } = useTranslation('benefits');
  const [rows, setRows] = useState<BenefitEnrollment[]>([]);
  const [plans, setPlans] = useState<BenefitPlan[]>([]);
  const [open, setOpen] = useState(false); const [busy, setBusy] = useState(false);
  const [f, setF] = useState<any>({ coverage_level: 'employee_only' });
  const load = () => listEnrollments(employeeId).then(setRows).catch(() => {});
  useEffect(() => { load(); listPlans().then((p) => setPlans(p.filter((x) => x.status === 'active'))).catch(() => {}); }, [employeeId]);
  const planName = useMemo(() => Object.fromEntries(plans.map((p) => [p.id, p.name])), [plans]);
  const save = async () => { setBusy(true); try { await hrEnroll({ employee_id: employeeId, benefit_plan_id: f.benefit_plan_id, coverage_level: f.coverage_level, effective_start_date: f.effective_start_date }); setOpen(false); setF({ coverage_level: 'employee_only' }); load(); } catch (e) { toast.error(getApiErrorMessage(e, t('err_enroll', 'Could not enroll in the plan.')), { duration: 6000 }); } finally { setBusy(false); } };
  const act = (p: Promise<any>) => runAction(p, load, t('err_update_enrollment', 'Could not update the enrollment.'));
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between"><CardTitle className="text-base">{t('plan_enrollments')}</CardTitle><Button size="sm" onClick={() => setOpen(true)} disabled={plans.length === 0}><Plus className="w-4 h-4 mr-1" /> {t('enroll')}</Button></CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow><TableHead>{t('col_plan')}</TableHead><TableHead>{t('col_coverage')}</TableHead><TableHead>{t('col_status')}</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {rows.map((e) => (
              <TableRow key={e.id}><TableCell className="font-medium">{planName[e.benefit_plan_id] || e.benefit_plan_id.slice(0, 8)}</TableCell><TableCell className="capitalize">{e.coverage_level.replace(/_/g, ' ')}</TableCell><TableCell><StatusBadge s={e.status} /></TableCell>
                <TableCell className="text-right space-x-2">{e.status === 'pending' && <Button size="sm" variant="outline" onClick={() => act(approveEnrollment(e.id))}>{t('approve')}</Button>}{(e.status === 'active' || e.status === 'approved') && <Button size="sm" variant="ghost" onClick={() => act(terminateEnrollment(e.id))}>{t('terminate')}</Button>}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">{t('no_enrollments')}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
      <FormModal open={open} title={t('enroll_in_plan')} busy={busy} onClose={() => setOpen(false)} onSubmit={save}>
        <Field label={t('col_plan')}><select className={sel} value={f.benefit_plan_id || ''} onChange={(e) => setF({ ...f, benefit_plan_id: e.target.value })}><option value="">{t('select')}</option>{plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></Field>
        <Field label={t('f_coverage_level')}><select className={sel} value={f.coverage_level} onChange={(e) => setF({ ...f, coverage_level: e.target.value })}>{COVERAGE.map((tp) => <option key={tp} value={tp}>{tp.replace(/_/g, ' ')}</option>)}</select></Field>
        <Field label={t('f_effective_start')}><Input type="date" value={f.effective_start_date || ''} onChange={(e) => setF({ ...f, effective_start_date: e.target.value })} /></Field>
      </FormModal>
    </Card>
  );
};

// ---- Tenant-wide audit tables (all employees) ------------------------------
const useEmpNames = () => {
  const [map, setMap] = useState<Record<string, string>>({});
  useEffect(() => {
    getAllEmployees().then((e: any) => setMap(Object.fromEntries((e || []).map((x: Employee) => [x.id, x.full_name || [x.first_name, x.last_name].filter(Boolean).join(' ') || x.email || x.id])))).catch(() => {});
  }, []);
  return map;
};
const StatusFilter: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
  const { t } = useTranslation('benefits');
  return (
    <select className={sel + ' w-40'} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{t('all_statuses')}</option>
      {['draft', 'approved', 'active', 'suspended', 'ended'].map((s) => <option key={s} value={s}>{s}</option>)}
    </select>
  );
};

const TenantAllowances: React.FC = () => {
  const { t } = useTranslation('benefits');
  const [rows, setRows] = useState<EmployeeAllowance[]>([]);
  const [status, setStatus] = useState('approved');
  const names = useEmpNames();
  const load = () => listAllAllowances(status || undefined).then(setRows).catch(() => {});
  useEffect(() => { load(); }, [status]);
  const act = (p: Promise<any>) => runAction(p, load, t('err_update_allowance', 'Could not update the allowance.'));
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-base">{t('all_allowances')}</CardTitle>
        <StatusFilter value={status} onChange={setStatus} />
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow><TableHead>{t('col_employee')}</TableHead><TableHead>{t('col_type')}</TableHead><TableHead>{t('col_name')}</TableHead><TableHead>{t('col_amount')}</TableHead><TableHead>{t('col_taxable')}</TableHead><TableHead>{t('col_status')}</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {rows.map((a) => (
              <TableRow key={a.id}>
                <TableCell className="font-medium">{names[a.employee_id] || a.employee_id.slice(0, 8)}</TableCell>
                <TableCell className="capitalize">{a.allowance_type}</TableCell>
                <TableCell>{a.name}</TableCell>
                <TableCell>{fmt(a.amount, a.currency)}</TableCell>
                <TableCell>{a.taxable ? t('yes') : t('no')}</TableCell>
                <TableCell><StatusBadge s={a.status} /></TableCell>
                <TableCell className="text-right space-x-2">{a.status === 'draft' && <Button size="sm" variant="outline" onClick={() => act(approveAllowance(a.id))}>{t('approve')}</Button>}{a.status !== 'ended' && <Button size="sm" variant="ghost" onClick={() => act(endAllowance(a.id))}>{t('end')}</Button>}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">{t('no_allowances')}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

const TenantDeductions: React.FC = () => {
  const { t } = useTranslation('benefits');
  const [rows, setRows] = useState<EmployeeDeduction[]>([]);
  const [status, setStatus] = useState('approved');
  const names = useEmpNames();
  const load = () => listAllDeductions(status || undefined).then(setRows).catch(() => {});
  useEffect(() => { load(); }, [status]);
  const act = (p: Promise<any>) => runAction(p, load, t('err_update_deduction', 'Could not update the deduction.'));
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-base">{t('all_deductions')}</CardTitle>
        <StatusFilter value={status} onChange={setStatus} />
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow><TableHead>{t('col_employee')}</TableHead><TableHead>{t('col_type')}</TableHead><TableHead>{t('col_name')}</TableHead><TableHead>{t('col_amount')}</TableHead><TableHead>{t('col_pretax')}</TableHead><TableHead>{t('col_status')}</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {rows.map((d) => (
              <TableRow key={d.id}>
                <TableCell className="font-medium">{names[d.employee_id] || d.employee_id.slice(0, 8)}</TableCell>
                <TableCell className="capitalize">{d.deduction_type.replace(/_/g, ' ')}</TableCell>
                <TableCell>{d.name}</TableCell>
                <TableCell>{fmt(d.amount, d.currency)}</TableCell>
                <TableCell>{d.pre_tax ? t('yes') : t('no')}</TableCell>
                <TableCell><StatusBadge s={d.status} /></TableCell>
                <TableCell className="text-right space-x-2">{d.status === 'draft' && <Button size="sm" variant="outline" onClick={() => act(approveDeduction(d.id))}>{t('approve')}</Button>}{d.status !== 'ended' && <Button size="sm" variant="ghost" onClick={() => act(endDeduction(d.id))}>{t('end')}</Button>}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">{t('no_deductions')}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default BenefitsPage;

// CompanyBenefits — company-wide allowances/benefits that apply to EVERY active
// employee (whole-company cost + each individual's pay). Supports benefits-in-kind
// (taxable non-cash: taxed via PAYE, not paid as cash).
const C_TYPES = ['transport', 'housing', 'meal', 'fuel', 'internet', 'airtime', 'medical', 'other'];
const CompanyBenefits: React.FC = () => {
  const { t } = useTranslation('benefits');
  const [rows, setRows] = useState<CompanyAllowance[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ allowance_type: 'transport', name: '', amount: '', taxable: true, in_kind: false });
  const [busy, setBusy] = useState(false);

  const reload = () => listCompanyAllowances().then(setRows).catch(() => setRows([])).finally(() => setLoading(false));
  useEffect(() => { reload(); }, []);

  const create = async () => {
    if (!form.name || !form.amount) return;
    setBusy(true);
    try {
      await createCompanyAllowance({
        allowance_type: form.allowance_type, name: form.name, amount: Number(form.amount),
        taxable: form.taxable, in_kind: form.in_kind,
      });
      setForm({ allowance_type: 'transport', name: '', amount: '', taxable: true, in_kind: false });
      reload();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('err_create_company', 'Could not add the company benefit.')), { duration: 6000 });
    } finally { setBusy(false); }
  };

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground p-6"><Loader2 className="w-4 h-4 animate-spin" /> {t('loading')}</div>;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Building2 className="w-5 h-5 text-orange-600" /> {t('tab_company', 'Company Benefits')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            {t('company_intro', 'Benefits that apply to every employee. They flow into each person’s payroll automatically. Mark a benefit “in kind” for a non-cash benefit (e.g. company housing) — it is taxed via PAYE but not paid as cash.')}
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('name', 'Name')}</TableHead>
                <TableHead>{t('type', 'Type')}</TableHead>
                <TableHead className="text-right">{t('amount', 'Amount')}</TableHead>
                <TableHead>{t('tax', 'Tax')}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">{r.name}</TableCell>
                  <TableCell className="capitalize">{r.allowance_type}</TableCell>
                  <TableCell className="text-right">{fmt(r.amount, r.currency)}</TableCell>
                  <TableCell className="space-x-1">
                    {r.in_kind && <Badge className="bg-purple-100 text-purple-700 border-0">{t('in_kind', 'In kind')}</Badge>}
                    {r.taxable ? <Badge className="bg-amber-100 text-amber-700 border-0">{t('taxable', 'Taxable')}</Badge> : <Badge className="bg-slate-100 text-slate-600 border-0">{t('non_taxable', 'Non-taxable')}</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => deleteCompanyAllowance(r.id).then(reload).catch((e) => toast.error(getApiErrorMessage(e, t('err_delete_company', 'Could not remove the company benefit.')), { duration: 6000 }))}><MinusCircle className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">{t('no_company', 'No company-wide benefits yet.')}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">{t('add_company', 'Add company benefit')}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-1.5">
              <Label>{t('name', 'Name')}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Staff transport" />
            </div>
            <div className="space-y-1.5">
              <Label>{t('type', 'Type')}</Label>
              <select className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.allowance_type} onChange={(e) => setForm({ ...form, allowance_type: e.target.value })}>
                {C_TYPES.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>{t('amount', 'Amount')}</Label>
              <Input type="number" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.taxable} onChange={(e) => setForm({ ...form, taxable: e.target.checked })} /> {t('taxable', 'Taxable')}</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.in_kind} onChange={(e) => setForm({ ...form, in_kind: e.target.checked })} /> {t('in_kind_benefit', 'Benefit in kind (non-cash)')}</label>
            <Button onClick={create} disabled={busy || !form.name || !form.amount}>
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />} {t('add', 'Add')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
